import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useRecordMetadataSchemaList } from "~/features/admin/recordMetadataSchema/hooks";
import { toRecordMetadataSchemaRowList } from "~/features/admin/recordMetadataSchema/utils/transformers";
import { v2Route } from "~/v2/routes";
import { RecordMetadataSchemaPresenter } from "./recordMetadataSchema.presenter";

const DEFAULT_PAGE_SIZE = 20;

export function RecordMetadataSchemaContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.recordMetadataSchema);
  const { data, refetch } = useRecordMetadataSchemaList();

  const rows = useMemo(
    () => toRecordMetadataSchemaRowList(data.recordMetadataPropertySchemaSets.map((r) => r.propertySchemaSet)),
    [data.recordMetadataPropertySchemaSets],
  );

  // The text input is driven by local state for instant feedback; the URL is a
  // background mirror (sharable / restored on reload + back-forward). We adopt the
  // URL value into local state only on a genuine external change (back/forward,
  // shared link), so our own writes never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedRecordMetadataSchema = params.selectedRecordMetadataSchema ?? null;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedRecordMetadataSchema: id })),
    [setParams],
  );

  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedRecordMetadataSchema: undefined })),
    [setParams],
  );

  return (
    <RecordMetadataSchemaPresenter
      rows={rows}
      search={search}
      onSearchChange={onSearchChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
      selectedRecordMetadataSchema={selectedRecordMetadataSchema}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
